import { salonService } from '../../../../_services/salon.service'
import { loadBrandKit, saveBrandKit } from '../email-composer/email/theme'

// The brand kit on the server (salon.emailBrandKit, read and written through
// GET/PUT salon/:id/settings), so it follows the salon to every device.
// localStorage (beu-email-brand-<salonId>) stays as the offline cache that
// every screen reads synchronously; this module keeps the two in step.
//
// - On load the server's kit wins and refreshes the cache.
// - A device holding a kit the server has never seen sends it up once.
// - Every save goes up too (debounced). Staff without salon settings rights
//   are refused by the server; their kit simply stays on this device.

const syncedKey = (salonId) => `beu-email-brand-synced-${salonId || 'default'}`
const isSynced = (salonId) => { try { return localStorage.getItem(syncedKey(salonId)) === '1' } catch (e) { return false } }
const markSynced = (salonId) => { try { localStorage.setItem(syncedKey(salonId), '1') } catch (e) { /* private mode */ } }

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/
const TOKEN = /^[A-Za-z0-9_-]{1,40}$/
const HTTPS = /^https:\/\/[^\s"'<>]+$/i
const TOKEN_KEYS = ['look', 'logoShape', 'display', 'text']

// Only what the server accepts (lib/module/v2/marketing/emailBrandKit.js), so
// one odd value from an old cache can't get the whole kit refused.
export const toServerKit = (kit) => {
  if (!kit || typeof kit !== 'object') return null
  const out = {}
  // null clears a setting on the server (undoing a first brand save)
  TOKEN_KEYS.forEach((k) => { if (kit[k] === null) out[k] = null; else if (typeof kit[k] === 'string' && TOKEN.test(kit[k])) out[k] = kit[k] })
  if (kit.accent === null) out.accent = null
  else if (typeof kit.accent === 'string' && HEX.test(kit.accent)) out.accent = kit.accent
  if (kit.logo === null) out.logo = null
  else if (typeof kit.logo === 'string' && HTTPS.test(kit.logo) && kit.logo.length <= 2048) out.logo = kit.logo
  if (typeof kit.logoShapeFor === 'string' && HTTPS.test(kit.logoShapeFor) && kit.logoShapeFor.length <= 2048) out.logoShapeFor = kit.logoShapeFor
  const num = (v, min, max) => { const n = Number(v); return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : undefined }
  if (kit.logoWidth !== undefined && kit.logoWidth !== null) { const n = num(kit.logoWidth, 16, 600); if (n !== undefined) out.logoWidth = n }
  if (kit.radius !== undefined && kit.radius !== null) { const n = num(kit.radius, 0, 64); if (n !== undefined) out.radius = n }
  if (typeof kit.name === 'string' && kit.name.length <= 120) out.name = kit.name
  // The salon's voice, as they confirmed it with Bailey. null clears it; left
  // out, the server keeps what it has (a kit save from elsewhere never wipes it)
  if (kit.tone === null) out.tone = null
  else if (typeof kit.tone === 'string' && kit.tone.trim() && kit.tone.length <= 400 && !/[<>]/.test(kit.tone)) out.tone = kit.tone.trim()
  if (kit.palette === null) out.palette = null
  else if (Array.isArray(kit.palette)) { const p = kit.palette.filter(c => typeof c === 'string' && HEX.test(c)).slice(0, 6); if (p.length) out.palette = p }
  if (kit.toneConfirmedAt === null) out.toneConfirmedAt = null
  else if (typeof kit.toneConfirmedAt === 'string' && !Number.isNaN(Date.parse(kit.toneConfirmedAt))) out.toneConfirmedAt = kit.toneConfirmedAt
  if (kit.button && typeof kit.button === 'object') {
    const b = {}
    Object.keys(kit.button).forEach((k) => { if (TOKEN.test(k) && typeof kit.button[k] === 'string' && TOKEN.test(kit.button[k])) b[k] = kit.button[k] })
    if (Object.keys(b).length) out.button = b
  }
  return Object.keys(out).length ? out : null
}

const putKit = async (salonId, kit) => {
  const fd = new FormData()
  fd.append('salonId', salonId)
  fd.append('emailBrandKit', JSON.stringify(kit))
  const res = await salonService.updateSalon(fd)
  const d = res && res.data
  const bad = !d || d.errorCode || Number(d.statusCode) === 0
    || (d.responseData && typeof d.responseData === 'object' && (d.responseData.errorCode || Number(d.responseData.statusCode) === 0))
  if (bad) throw new Error('not saved')
}

const timers = {}
const queued = {}
const inflight = {}
const send = (salonId) => {
  const clean = queued[salonId]
  delete queued[salonId]
  delete timers[salonId]
  const p = putKit(salonId, clean).then(() => markSynced(salonId))
  inflight[salonId] = p
  const done = () => { if (inflight[salonId] === p) delete inflight[salonId] }
  p.then(done, done)
  return p
}
// Send a kit up (debounced per salon). Returns nothing; failures are quiet
// because the kit is already saved on this device.
export const pushBrandKit = (salonId, kit, { delay = 800 } = {}) => {
  if (!salonId) return
  const clean = toServerKit(kit)
  if (!clean) return
  clearTimeout(timers[salonId])
  queued[salonId] = clean
  timers[salonId] = setTimeout(() => { send(salonId).catch(() => {}) }, delay)
}

// Send a waiting kit now and wait for it (and any send already under way).
// Rejects when the server refused it: callers that copy the salon record
// elsewhere (Apply to all locations) must not copy the kit from before.
export const flushBrandKit = async (salonId) => {
  if (!salonId) return
  if (timers[salonId]) { clearTimeout(timers[salonId]); return send(salonId) }
  if (inflight[salonId]) return inflight[salonId]
}

// Given the salon settings record, settle which kit this device uses.
// Returns the kit to show (server's when it has one), or null.
export const hydrateBrandKit = (salonId, salon) => {
  if (!salonId) return null
  const server = salon && salon.emailBrandKit
  if (server && typeof server === 'object' && Object.keys(server).length) {
    saveBrandKit(salonId, server)
    markSynced(salonId)
    return server
  }
  const local = loadBrandKit(salonId)
  // Only when the salon record actually loaded: a failed read is not "the
  // server has no kit".
  if (salon && local && Object.keys(local).length && !isSynced(salonId)) pushBrandKit(salonId, local, { delay: 0 })
  return local
}
